import React, { useState, useEffect } from 'react';
import { matchPath } from 'react-router-dom';
import { useSyncContentsAssociatedSkillTemplates } from 'components/Skills/queries/skillTemplates';
import {
  useSyncTaggableToReadinessSkills,
  useTagAllContentInPath,
} from 'components/Skills/queries/skillTags';
import { withSkillsClientWrapper } from 'components/shared/Skills/SkillsClientProvider';
import { withReactQueryWrapper } from 'components/shared/ReactQuery/ReactQueryProvider';
import { useToggle, usePermissions, useFeatureFlags } from 'hooks';
import Error from 'components/InPersonTraining/shared/Error';
import { Icon } from 'ui';
import { TEXT_VARIANTS, Text } from 'components/ui/Typography';
import Section, { SectionHeader } from 'components/PathBuilder/Sidebar/Section';
import { TYPES, REACT_QUERY_STATUS } from 'constants/config';
import ViewPathAssociatedSkills from './ViewPathAssociatedSkills';
import EditPathAssociatedSkills from './EditPathAssociatedSkills';

export const PathAssociatedSkillsBase = () => {
  const [editMode, toggleEditMode] = useToggle(false);
  const match = () =>
    matchPath(window.location.pathname, { path: '/paths/:pathId' });
  const pathId = match()?.params?.pathId;
  const hasSkillsEnabled = useFeatureFlags('readiness-enable-skills-2-0');
  const { isPermitted: isSkillsAdmin } = usePermissions({
    type: 'skill',
    action: 'skills_admin',
  });

  const syncMutation = useSyncContentsAssociatedSkillTemplates({
    contentId: parseInt(pathId.split('-')[0], 10),
    contentType: TYPES.PATH,
  });
  const tagContentInPathMutation = useTagAllContentInPath({
    pathId: parseInt(pathId.split('-')[0], 10),
  });
  const [selectedSkills, setSelectedSkills] = useState([]);

  const syncTagsMutation = useSyncTaggableToReadinessSkills({
    contentId: parseInt(pathId.split('-')[0], 10),
    contentType: TYPES.PATH,
  });
  const [selectedSkillTags, setSelectedSkillTags] = useState([]);

  const showEditButton = () => {
    if (isSkillsAdmin) {
      if (!hasSkillsEnabled && selectedSkills?.length > 0) {
        return true;
      }
      if (hasSkillsEnabled && selectedSkillTags?.length > 0) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (
      !hasSkillsEnabled &&
      syncMutation.status === REACT_QUERY_STATUS.SUCCESS
    ) {
      toggleEditMode();
    }
  }, [syncMutation.status, toggleEditMode, hasSkillsEnabled]);

  useEffect(() => {
    if (
      hasSkillsEnabled &&
      syncTagsMutation.status === REACT_QUERY_STATUS.SUCCESS
    ) {
      toggleEditMode();
    }
  }, [syncTagsMutation.status, toggleEditMode, hasSkillsEnabled]);

  async function handleSubmit() {
    try {
      if (hasSkillsEnabled) {
        await syncTagsMutation.mutate({
          taggableType: TYPES.PATH,
          taggableId: parseInt(pathId.split('-')[0], 10),
          readinessSkillIds: selectedSkillTags.map(sk => sk.id),
        });
      } else {
        await syncMutation.mutate({
          lessonlyContentId: parseInt(pathId.split('-')[0], 10),
          lessonlyContentType: TYPES.PATH,
          skillTemplateIds: selectedSkills.map(sk => sk.id),
        });
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function onTagAllContentsInPath(readinessSkillId, tagName) {
    try {
      if (hasSkillsEnabled) {
        await tagContentInPathMutation.mutate({
          pathId: parseInt(pathId.split('-')[0], 10),
          readinessSkillId,
        });
        // eslint-disable-next-line no-alert
        alert(
          t('tags.tag_all_content_in_path_alert', {
            tag_name: tagName,
          }),
        );
      }
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <>
      <Section>
        <SectionHeader className="d-f jc-sb">
          <Text component="h2" variant={TEXT_VARIANTS.BODY_BOLD}>
            {hasSkillsEnabled
              ? t('skills.general.skill_tags.title')
              : t('skills.general.title')}
          </Text>
          {showEditButton() && !editMode && (
            <button
              type="button"
              className="u-linkBtn fz-base fw-n"
              onClick={toggleEditMode}
            >
              <Icon name="edit" className="mr-4" />
              {t('common.edit')}
            </button>
          )}
        </SectionHeader>
        {editMode && (
          <EditPathAssociatedSkills
            selectedSkills={selectedSkills}
            setSelectedSkills={setSelectedSkills}
            selectedSkillTags={selectedSkillTags}
            setSelectedSkillTags={setSelectedSkillTags}
            handleSubmit={handleSubmit}
            onTagAllContentsInPath={onTagAllContentsInPath}
          />
        )}
        {!editMode && (
          <ViewPathAssociatedSkills
            setSelectedSkills={setSelectedSkills}
            setSelectedSkillTags={setSelectedSkillTags}
            toggleEditing={toggleEditMode}
          />
        )}
        {(syncMutation.isError ||
          syncTagsMutation.isError ||
          tagContentInPathMutation.isError) && (
          <Error message={t('common.your_save_failed_please_try_again')} />
        )}
      </Section>
    </>
  );
};

export default withSkillsClientWrapper(
  withReactQueryWrapper(PathAssociatedSkillsBase),
);
